import React, { useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import { Layout, Seo } from '@layout';
import { WonderfulWebsiteSectionV2 } from '@sections/save-the-date';
import { AbcSection } from '@sections/shared/AbcSection';
import { FAQSection } from '@sections/shared/FAQSection';
import { HighQualityPrintsSection as HighQualityPrintsSectionV2 } from '@sections/shared/HighQualityPrintsSection';
import { HowItWorksSection as HowItWorksSectionV2 } from '@sections/shared/HowItWorksSection';
import { faqQuestionsData } from '@data-ts/components/FaqQuestions';
import { saveTheDateDesignsCanonicalUrl } from '@utils/gallery.util';
import { saveTheDateDesignsPageData as pageData } from '@data-ts/pages/save-the-date';
import { productTypes } from '@data-ts/components/GalleryComponents/GalleryPageContent';
import { createFilterGroupsProcessor, useProcessPrintCards } from '@utils/joyPrint.utils';
import { saveTheDatePhotoECards } from '@components/GalleryComponents/GalleryComponents.constants';
import { GalleryPageContent } from '@components/GalleryComponents/GalleryPageContent/GalleryPageContent';
import { HeroSectionWithProps } from '@sections/shared/HeroSectionWithProps';
import { ValuePropsArg } from '@components/ValuePropsSection';

export const query = graphql`
  {
    bliss {
      stationeryTemplatesContainer(filter: { categories: saveTheDate }) {
        pagedResults {
          edges {
            node {
              themeId
              thumbnailUrl
              approved
              enabled
              category
            }
          }
        }
      }
      themeVariants(variantTypes: [savethedate]) {
        variationType
        imageUrl
        printUrl
        vendor
        tags
        colors
        font
        variantColor
        variantName
        variantThemeId
        theme {
          id
          name
          themeId
          marketingData {
            variationType
            imageUrl
            printUrl
            vendor
            tags
            colors
            font
            variantColor
            variantName
            variantThemeId
            theme {
              name
              themeId
            }
          }
        }
      }
    }
  }
`;

export const SAVE_THE_DATE_VALUE_PROPS: ValuePropsArg[] = [
  {
    id: 'designDreamCards',
    icon: 'designCard',
    title: 'Design Your Dream Cards',
    description: 'Endless designs to match <br/> your wedding style and the <br/> best customization tools.',
  },
  {
    id: 'premiumPaperAndDigital',
    icon: 'premiumPaper',
    title: 'Premium Paper & Digital',
    description: 'From traditional paper <br/> cards to free online Save <br/> the Dates, do it all.',
  },
  {
    id: 'addressingMadeEasy',
    icon: 'addressing',
    title: 'Addressing Made Easy',
    description: 'Save time getting guest <br/> info with the magic of <br/> Contact Collector.',
  },
  {
    id: 'matchWeddingWebsite',
    icon: 'weddingWebsiteIcon',
    title: 'Match Wedding Website',
    description: 'Seamlessly design your <br/> Save the Dates to match <br/> your wedding website.',
  },
];

export const SAVE_THE_DATE_VALUE_PROPS_MOBILE: ValuePropsArg[] = [
  {
    id: 'designDreamCards',
    icon: 'designCard',
    title: 'Design Your <br/> Dream Cards',
  },
  {
    id: 'premiumPaperAndDigital',
    icon: 'premiumPaper',
    title: 'Premium <br/> Paper & Digital',
  },
  {
    id: 'addressingMadeEasy',
    icon: 'addressing',
    title: 'Addressing <br/> Made Easy',
  },
  {
    id: 'matchWeddingWebsite',
    icon: 'weddingWebsiteIcon',
    title: 'Match Wedding <br/> Website',
  },
];

const CATEGORIES_IMAGES_DATA = {
  bold: 'save-the-date-gallery/styles/bold.png',
  classic: 'save-the-date-gallery/styles/classic.png',
  cultural: 'save-the-date-gallery/styles/cultural.png',
  destination: 'save-the-date-gallery/styles/destination.png',
  'floral-and-botanical': 'save-the-date-gallery/styles/floral_botanical-v2.png',
  metallic: 'save-the-date-gallery/styles/metallic.png',
  modern: 'save-the-date-gallery/styles/modern-v2.png',
  photo: 'save-the-date-gallery/styles/photo.png',
  rustic: 'save-the-date-gallery/styles/rustic.png',
  seasonal: 'save-the-date-gallery/styles/seasonal.png',
  textured: 'save-the-date-gallery/styles/textured.png',
};

const page: MarcomPageName = 'save-the-date';

const SaveTheDate = ({ data }: { data: BlissGalleryECardsThemesData }) => {
  const { faq, highQualityPrintsSectionV2, howItWorksSectionV2, hero } = pageData;
  const { cards, setShowDesignsWithMatchingPrints } = useProcessPrintCards(data, 'savethedate', saveTheDatePhotoECards);

  const [chromeless, setChromeless] = useState(false);

  const processFilterGroups = useMemo(() => {
    return createFilterGroupsProcessor(productTypes.saveTheDate, data.bliss);
  }, [data]);

  return (
    <Layout
      isTransparentHeader={true}
      transparentJoyLogoColor="mono7"
      transparentHeaderButtonVariant="defaultPrimeWeddingWebsites"
      hideValueProps
      page={page}
      chromeless={chromeless}
    >
      <Seo title={pageData.seo.title} description={pageData.seo.content} canonicalURL={saveTheDateDesignsCanonicalUrl} />
      <HeroSectionWithProps
        page={page}
        sectionId="Save_The_Date_Hero"
        ctaButtonId="Save_The_Date_Hero_CTA"
        heroData={hero}
        valueProps={SAVE_THE_DATE_VALUE_PROPS}
        valuePropsMobile={SAVE_THE_DATE_VALUE_PROPS_MOBILE}
        valuePropsElementId="save-the-date-props"
      />
      <GalleryPageContent
        page={page}
        productType={productTypes.saveTheDate}
        cards={cards}
        hasPhotoECards
        processFilterGroups={processFilterGroups}
        showModal={isModalOpened => {
          setChromeless(isModalOpened);
        }}
        onShowDesignsWithMatchingPrintsToggle={setShowDesignsWithMatchingPrints}
        displayHeroSection={false}
        initialProductCount={12}
        displayFilterTab={false}
        defaultProductType={productTypes.prints}
        sectionId="gallery-grid-wrapper"
        displayCategoryImages
        categoryImagesData={CATEGORIES_IMAGES_DATA}
        hideCategoryImagesScrollbar
      />
      <HighQualityPrintsSectionV2 highQualityPrintsSectionData={highQualityPrintsSectionV2} />
      <HowItWorksSectionV2 howItWorksSectionData={howItWorksSectionV2} />
      <WonderfulWebsiteSectionV2 />
      <FAQSection
        id="save-the-date-faq"
        title={faq.title}
        description={faq.description}
        questionNodes={faqQuestionsData.saveTheDateDesignV2}
      />
      <AbcSection page={page} abcSectionId="Save_the_Date_Design_ABC_CTA" />
    </Layout>
  );
};

export default SaveTheDate;
